import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@devhop/ui";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import type { VoidCreditInvoicePreviewDTO } from "../api/client";
import {
  useVoidCreditInvoiceMutation,
  useVoidCreditInvoicePreviewQuery,
} from "../api/queries";

export type VoidCreditInvoiceDialogProps = {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

function ImpactSummary({ preview }: { preview: VoidCreditInvoicePreviewDTO }) {
  if (!preview.canVoid) {
    return (
      <p className="text-destructive text-sm">
        {preview.blockReason ?? "ບໍ່ສາມາດຍົກເລີກໃບນີ້"}
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-4 text-sm">
      <p className="font-medium">ຜົນກະທົບທີ່ຈະຖືກຍ້ອນກັບ:</p>
      <ul className="list-inside list-disc space-y-1 text-muted-foreground">
        {preview.activePayments > 0 && (
          <li>ຍົກເລີກການຮັບເງິນ {preview.activePayments} ຄັ້ງ</li>
        )}
        {preview.classPackages > 0 && (
          <li>ລຶບແພັກເກດຄລາດສ໌ {preview.classPackages} ລາຍການ</li>
        )}
        {preview.privateLessonPackages > 0 && (
          <li>
            ລຶບແພັກເກດຮຽນສ່ວນຕົວ {preview.privateLessonPackages} ລາຍການ
          </li>
        )}
        {preview.roomBookings > 0 && (
          <li>ຍົກເລີກຈອງຫ້ອງ {preview.roomBookings} ລາຍການ</li>
        )}
        {preview.estimatedBookings > 0 && (
          <li>ການຈອງ PL: {preview.estimatedBookings} ລາຍການ</li>
        )}
        {preview.estimatedAttendances > 0 && (
          <li>ບັນທຶກເຊັກຊື່: {preview.estimatedAttendances} ລາຍການ</li>
        )}
      </ul>
      <p className="pt-2 text-muted-foreground text-xs">
        ໃບຊຳລະບາງສ່ວນຈະຖືກຍົກເລີກ — ຂໍ້ມູນນັກຮຽນຈະບໍ່ຖືກລຶບ
      </p>
    </div>
  );
}

export function VoidCreditInvoiceDialog({
  invoiceId,
  open,
  onOpenChange,
  onSuccess,
}: VoidCreditInvoiceDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: preview, isLoading: isPreviewLoading } =
    useVoidCreditInvoicePreviewQuery(invoiceId, open);
  const voidMutation = useVoidCreditInvoiceMutation(invoiceId);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("ກະລຸນາປ້ອນເຫດຜົນ");
      return;
    }
    if (!preview?.canVoid) return;

    setError(null);
    try {
      await voidMutation.mutateAsync({ reason: trimmed });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const msg =
        e instanceof Error && e.message
          ? e.message
          : "ຍົກເລີກໃບຊຳລະບາງສ່ວນລົ້ມເຫຼວ";
      setError(msg);
    }
  };

  const isSubmitting = voidMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        onPointerDownOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>ຍົກເລີກໃບຊຳລະບາງສ່ວນ</DialogTitle>
          <p className="text-muted-foreground text-sm">
            ຍ້ອນກັບການຂາຍ/ຈອງທີ່ຜູກກັບໃບນີ້ — ບໍ່ສາມາດຍ້ອນກັບໄດ້
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isPreviewLoading ? (
            <div className="flex justify-center py-6">
              <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : preview ? (
            <ImpactSummary preview={preview} />
          ) : (
            <p className="text-destructive text-sm">ບໍ່ສາມາດໂຫຼດ preview</p>
          )}

          {preview?.canVoid ? (
            <div className="space-y-2">
              <Label htmlFor="void-credit-invoice-reason">
                ເຫດຜົນ <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="void-credit-invoice-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="ປ້ອນເຫດຜົນການຍົກເລີກ..."
                rows={4}
                className="resize-none"
                disabled={isSubmitting}
              />
            </div>
          ) : null}

          {error ? <p className="text-destructive text-sm">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            ປິດ
          </Button>
          {preview?.canVoid ? (
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isSubmitting || isPreviewLoading}
            >
              {isSubmitting ? "ກຳລັງຍົກເລີກ..." : "ຢືນຢັນຍົກເລີກ"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
